<template>
    <div class="results">
        <div class="slot-lab">Успешные кейсы новых клиентов</div>
        <p class="diag-sub">
            Реальные результаты салонов красоты, клиник, барбершопов и ногтевых студий, которые недавно запустили
            эти сценарии в DH.
        </p>
        <div class="case-slider">
            <div ref="caseTrack" class="case-track" @scroll="onCasesScroll">
                <div
                    v-for="item in successCases"
                    :key="item.caption + item.metric"
                    class="case-card"
                    :style="{
                        '--ac': getCaseAccentColor(item.caption),
                        '--act': getCaseAccentColor(item.caption) + '1A',
                    }"
                >
                    <div class="case-head">
                        <span class="case-biz">{{ getCaseBusinessInitials(item.caption) }}</span>
                        <span class="case-cat">
                            <v-icon size="13">{{ getCaseCategoryIcon(item.metric) }}</v-icon>
                            {{ getCaseCategoryLabel(item.metric) }}
                        </span>
                        <span class="case-up">
                            <v-icon class="case-up__icon" size="12">mdi-trending-up</v-icon>
                            {{ item.up }}
                        </span>
                    </div>
                    <div class="case-metric-lab">{{ item.metric }}</div>
                    <div v-if="getCaseCategory(item.metric) === 'seg'" class="case-seg">
                        <div class="case-seg-row">
                            <span class="case-seg-k">было</span>
                            <div class="case-seg-track">
                                <div
                                    class="case-seg-fill was"
                                    :style="{ width: Math.max(6, parseCasePercent(item.was)) + '%' }"
                                ></div>
                            </div>
                        </div>
                        <div class="case-seg-row">
                            <span class="case-seg-k">стало</span>
                            <div class="case-seg-track">
                                <div
                                    class="case-seg-fill"
                                    :style="{ width: Math.max(6, parseCasePercent(item.now)) + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>
                    <svg
                        v-else-if="getCaseCategory(item.metric) === 'rev'"
                        class="case-area"
                        viewBox="0 0 100 58"
                        preserveAspectRatio="none"
                    >
                        <polygon :points="caseAreaPolygon(item.bars)" fill="var(--act)" />
                        <polyline
                            :points="caseAreaPoints(item.bars)"
                            fill="none"
                            stroke="var(--ac)"
                            stroke-width="2.4"
                            vector-effect="non-scaling-stroke"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div v-else class="case-chart">
                        <div
                            v-for="(bar, index) in item.bars"
                            :key="index"
                            class="case-bar"
                            :style="{ height: bar + '%', opacity: (0.4 + index * 0.15).toFixed(2) }"
                        ></div>
                    </div>
                    <div class="case-ba">
                        <span class="case-was">было {{ item.was }}</span>
                        <span class="case-arr">→</span>
                        <span class="case-now">{{ item.now }}</span>
                    </div>
                    <div class="case-cap">{{ item.caption }}</div>
                </div>
            </div>
        </div>
        <StarterPackCarouselNav
            :current-page="currentCasePage"
            :page-indexes="casePageIndexes"
            @prev="goToCasePage(currentCasePage - 1)"
            @next="goToCasePage(currentCasePage + 1)"
            @go-to="goToCasePage"
        />
        <div class="results-subhead">Что отвечают клиенты</div>
        <div class="case-slider">
            <div ref="reviewTrack" class="case-track" @scroll="onReviewsScroll">
                <div
                    v-for="review in reviews"
                    :key="review.name + review.tag + review.inboundTime"
                    class="chat"
                >
                    <div class="chat-head">
                        <div class="chat-av" :style="{ background: review.avatarColor }">
                            {{ review.initials }}
                        </div>
                        <div class="chat-meta">
                            <div class="chat-name">
                                {{ review.name }}
                                <span v-if="review.heart" class="hh">♥</span>
                            </div>
                            <div class="chat-status">был(а) недавно</div>
                        </div>
                    </div>
                    <div class="chat-body">
                        <div v-if="review.outbound" class="bubble out">
                            {{ review.outbound }}<span class="time">{{ review.outboundTime }} ✓✓</span>
                        </div>
                        <div :class="['bubble in', { 'react-wrap': review.heart }]">
                            {{ review.inbound }}<span class="time">{{ review.inboundTime }}</span>
                            <span v-if="review.heart" class="react">❤️</span>
                        </div>
                    </div>
                    <div class="chat-tag">
                        <v-icon class="chat-tag__icon" size="11">mdi-reply</v-icon>
                        Ответ на «{{ review.tag }}»
                    </div>
                </div>
            </div>
        </div>
        <StarterPackCarouselNav
            :current-page="currentReviewPage"
            :page-indexes="reviewPageIndexes"
            review-mode
            @prev="goToReviewPage(currentReviewPage - 1)"
            @next="goToReviewPage(currentReviewPage + 1)"
            @go-to="goToReviewPage"
        />
    </div>
</template>

<script>
import {
    getCaseAccentColor,
    getCaseBusinessInitials,
    getCaseCategory,
    getCaseCategoryIcon,
    getCaseCategoryLabel,
    parseCasePercent,
} from '../api'
import { caseAreaPoints, caseAreaPolygon } from '../utils/scenarioUi'
import horizontalCarouselMixin from '../mixins/horizontalCarouselMixin'
import StarterPackCarouselNav from './StarterPackCarouselNav.vue'

export default {
    name: 'StarterPackResultsSection',
    components: { StarterPackCarouselNav },
    mixins: [horizontalCarouselMixin],
    props: {
        successCases: {
            type: Array,
            required: true,
        },
        reviews: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            currentCasePage: 0,
            caseScrollSyncEnabled: true,
            caseScrollRaf: null,
            caseScrollUnlockTimer: null,
            currentReviewPage: 0,
            reviewScrollSyncEnabled: true,
            reviewScrollRaf: null,
            reviewScrollUnlockTimer: null,
        }
    },
    computed: {
        casePages() {
            return Math.max(1, Math.ceil(this.successCases.length / 3))
        },
        casePageIndexes() {
            return [...Array(this.casePages).keys()]
        },
        reviewPages() {
            return Math.max(1, Math.ceil(this.reviews.length / 3))
        },
        reviewPageIndexes() {
            return [...Array(this.reviewPages).keys()]
        },
    },
    beforeDestroy() {
        clearTimeout(this.caseScrollUnlockTimer)
        clearTimeout(this.reviewScrollUnlockTimer)
        if (this.caseScrollRaf) {
            cancelAnimationFrame(this.caseScrollRaf)
        }
        if (this.reviewScrollRaf) {
            cancelAnimationFrame(this.reviewScrollRaf)
        }
    },
    methods: {
        getCaseCategory,
        getCaseAccentColor,
        getCaseBusinessInitials,
        getCaseCategoryLabel,
        getCaseCategoryIcon,
        parseCasePercent,
        caseAreaPoints,
        caseAreaPolygon,
        goToCasePage(page) {
            this.goToCarouselPage({
                trackRef: 'caseTrack',
                page,
                pagesCount: this.casePages,
                setPage: value => {
                    this.currentCasePage = value
                },
                lockSync: () => {
                    this.lockCarouselScrollSync({
                        syncEnabledKey: 'caseScrollSyncEnabled',
                        unlockTimerKey: 'caseScrollUnlockTimer',
                        trackRef: 'caseTrack',
                    })
                },
            })
        },
        onCasesScroll() {
            this.onCarouselScroll({
                trackRef: 'caseTrack',
                syncEnabledKey: 'caseScrollSyncEnabled',
                rafKey: 'caseScrollRaf',
                pagesCount: this.casePages,
                currentPageKey: 'currentCasePage',
            })
        },
        goToReviewPage(page) {
            this.goToCarouselPage({
                trackRef: 'reviewTrack',
                page,
                pagesCount: this.reviewPages,
                setPage: value => {
                    this.currentReviewPage = value
                },
                lockSync: () => {
                    this.lockCarouselScrollSync({
                        syncEnabledKey: 'reviewScrollSyncEnabled',
                        unlockTimerKey: 'reviewScrollUnlockTimer',
                        trackRef: 'reviewTrack',
                    })
                },
            })
        },
        onReviewsScroll() {
            this.onCarouselScroll({
                trackRef: 'reviewTrack',
                syncEnabledKey: 'reviewScrollSyncEnabled',
                rafKey: 'reviewScrollRaf',
                pagesCount: this.reviewPages,
                currentPageKey: 'currentReviewPage',
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/vars.sass';

.results {
    min-width: 0;
    padding: 24px 24px 26px;
    margin-top: 8px;
    background: linear-gradient(180deg, #f0f7f2 0%, #edf5f0 100%);
    border: 1px solid #d8eadf;
    border-radius: 18px;
    box-sizing: border-box;
}

.slot-lab {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    color: #2e8b57;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.slot-lab::after {
    flex: 1;
    height: 1px;
    background: #cbe5d5;
    content: '';
}

.diag-sub {
    margin-bottom: 18px;
    color: #5e8570;
    font-size: 12.5px;
    line-height: 1.5;
}

.case-slider {
    overflow: hidden;
}

.case-track {
    display: flex;
    gap: 12px;
    padding-bottom: 4px;
    margin-bottom: 14px;
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
}

.case-track::-webkit-scrollbar {
    display: none;
}

.case-card {
    display: flex;
    flex: 0 0 calc((100% - 24px) / 3);
    flex-direction: column;
    gap: 13px;
    padding: 17px 18px 16px;
    scroll-snap-align: start;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 13px;
    --ac: #1fbf63;
    --act: #1fbf631a;
}

.case-head {
    display: flex;
    align-items: center;
    gap: 8px;
}

.case-biz {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    color: var(--ac);
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 11px;
    font-weight: 800;
    background: var(--act);
    border-radius: 9px;
}

.case-cat {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--ac);
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;

    .v-icon {
        color: var(--ac) !important;
    }
}

.case-metric-lab {
    min-height: 36px;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
}

.case-up {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: auto;
    padding: 4px 9px 4px 7px;
    color: var(--ac);
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 800;
    background: var(--act);
    border-radius: 999px;
    white-space: nowrap;
}

.case-up__icon {
    color: var(--ac) !important;
}

.case-chart {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    height: 58px;
}

.case-bar {
    flex: 1;
    min-width: 0;
    background: var(--ac);
    border-radius: 5px 5px 0 0;
}

.case-area {
    display: block;
    width: 100%;
    height: 58px;
}

.case-seg {
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: center;
    height: 58px;
}

.case-seg-row {
    display: flex;
    align-items: center;
    gap: 9px;
}

.case-seg-k {
    flex-shrink: 0;
    width: 32px;
    color: $muted-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
}

.case-seg-track {
    flex: 1;
    height: 9px;
    overflow: hidden;
    background: #ebeff4;
    border-radius: 999px;
}

.case-seg-fill {
    height: 100%;
    background: var(--ac);
    border-radius: 999px;

    &.was {
        background: #c7cdda;
    }
}

.case-track .chat {
    flex: 0 0 calc((100% - 24px) / 3);
    scroll-snap-align: start;
}

.case-ba {
    display: flex;
    align-items: baseline;
    gap: 9px;
}

.case-was,
.case-arr,
.case-cap {
    color: $muted-new;
}

.case-was {
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 600;
}

.case-now {
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 20px;
    font-weight: 900;
}

.case-cap {
    padding-top: 11px;
    margin-top: auto;
    font-size: 11px;
    line-height: 1.4;
    border-top: 1px solid $border-light-new;
}

.results-subhead {
    display: flex;
    align-items: center;
    gap: 9px;
    margin: 0 0 14px 1px;
    color: #2e8b57;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 800;
}

.results-subhead::before {
    width: 13px;
    height: 13px;
    border-radius: 5px;
    background: #2e8b57;
    content: '';
}

.chat {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    border: 1px solid #dcebe2;
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(38, 166, 91, 0.06);
}

.chat-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 13px;
    background: #f7f9fc;
    border-bottom: 1px solid $border-light-new;
}

.chat-av {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    color: #fff;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 12.5px;
    font-weight: 700;
    border-radius: 50%;
}

.chat-name {
    display: flex;
    align-items: center;
    gap: 5px;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 700;
}

.chat-status {
    margin-top: 1px;
    color: #8fa1c0;
    font-size: 10.5px;
}

.chat-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    padding: 13px;
    background: #e9eff5;
}

.bubble {
    max-width: 90%;
    padding: 8px 11px 6px;
    border-radius: 14px;
    font-size: 12px;
    line-height: 1.42;
}

.bubble.out {
    align-self: flex-end;
    color: #fff;
    background: #4a90e2;
    border-bottom-right-radius: 5px;
}

.bubble.in {
    align-self: flex-start;
    color: $text-color-new;
    background: #fff;
    border-bottom-left-radius: 5px;
    box-shadow: 0 1px 1.5px rgba(26, 26, 46, 0.07);
}

.bubble .time {
    display: block;
    margin-top: 3px;
    font-size: 9px;
    text-align: right;
    opacity: 0.7;
}

.bubble.out .time {
    color: #fff;
}

.bubble.in .time {
    color: $muted-light-new;
}

.react-wrap {
    position: relative;
}

.react {
    position: absolute;
    bottom: -9px;
    left: 9px;
    padding: 1px 5px;
    font-size: 10px;
    background: #fff;
    border-radius: 999px;
    box-shadow: 0 1px 3px rgba(26, 26, 46, 0.14);
}

.chat-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 9px 13px;
    color: #2e8b57;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 10px;
    font-weight: 700;
    background: #fff;
    border-top: 1px solid $border-light-new;
}

.chat-tag__icon {
    flex-shrink: 0;
    color: #2e8b57 !important;
}

@media (max-width: 760px) {
    .case-track .case-card,
    .case-track .chat {
        flex-basis: 86%;
    }

    .results {
        padding: 18px 14px 20px;
        border-radius: 14px;
    }
}
</style>
